import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Drawer,
  EmptyState,
  Icon,
  IconBtn,
  Spinner,
  useToast,
} from "../../../design-system";
import {
  Tabs,
} from "../../../design-system";
import {
  getJornadaLaboral,
  getTimeTrackingSettings,
  enqueueReport,
  listReportJobs,
  downloadReportJob,
} from "../../../helpers/kronaly_helper";
import useCurrentMembership from "../../../helpers/useCurrentMembership";
import { formatTimeHHMM, todayYmdInTz, DEFAULT_TZ } from "../../../helpers/datetime";
import { useStartTour } from "../../../tours/TourContext";
import CorregirJornadaModal from "./CorregirJornadaModal";
import BandejaCorrecciones from "./BandejaCorrecciones";
import CalendarioMes from "./CalendarioMes";

const MONTH_NAMES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];
const WEEKDAY_SHORT = ["lun", "mar", "mié", "jue", "vie", "sáb", "dom"];
const WEEKDAY_LONG = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

const STATUS_META = {
  ok:       { label: "Correcto",       tone: "success", icon: "CheckCircle2" },
  incident: { label: "Con incidencia", tone: "warning", icon: "AlertTriangle" },
  review:   { label: "Sin marcaje",    tone: "danger",  icon: "AlertTriangle" },
  current:  { label: "En curso",       tone: "info",    icon: "Clock" },
};

const unwrap = (r) => {
  const inner = r?.data ?? r;
  return inner?.data ?? inner;
};

function pad(n) { return String(n).padStart(2, "0"); }
function fmtDur(min) {
  if (min == null) return "—";
  const sign = min < 0 ? "-" : "";
  const abs = Math.abs(min);
  return `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}
function diffStr(diff) {
  if (diff == null) return "—";
  const sign = diff >= 0 ? "+" : "−";
  const abs = Math.abs(diff);
  return `${sign}${Math.floor(abs / 60)}h ${abs % 60}m`;
}
function ymdToParts(ymd) {
  const [y, m, d] = ymd.split("-").map((v) => parseInt(v, 10));
  return { y, m, d };
}
function ymdLocalDate(ymd) {
  const { y, m, d } = ymdToParts(ymd);
  return new Date(y, m - 1, d);
}
function dayOfWeekIdx(ymd) {
  // 0=Lun … 6=Dom
  const d = ymdLocalDate(ymd);
  const jsDow = d.getDay();
  return (jsDow + 6) % 7;
}
function monthLabel(ymd) {
  const { y, m } = ymdToParts(ymd);
  return `${MONTH_NAMES[m - 1]} ${y}`;
}
function firstOfMonth(ymd) {
  const { y, m } = ymdToParts(ymd);
  return `${y}-${pad(m)}-01`;
}
function addMonths(firstYmd, delta) {
  const { y, m } = ymdToParts(firstYmd);
  const total = (y * 12 + (m - 1)) + delta;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  return `${ny}-${pad(nm)}-01`;
}

/**
 * Enriquece cada día del response con campos derivados para la UI.
 * Status:
 *  - planned_minutes==0 → weekend/no-laboral (sin status)
 *  - record con segmento abierto (sin clock_out) → current
 *  - planned>0 && worked==0 && date<today → review
 *  - worked > 0 → ok | incident según balance
 */
function classifyDay(day, todayYmd, tz) {
  const planned = day.planned_minutes || 0;
  const worked = day.worked_minutes || 0;
  const segments = day.record?.segments || [];
  const breaks = day.record?.breaks || [];
  const totalBreakMin = breaks.reduce((acc, b) => {
    const start = new Date(b.start_at);
    const end = new Date(b.end_at);
    return acc + Math.max(0, Math.round((end - start) / 60000));
  }, 0);

  const firstIn = segments[0]?.clock_in_at || null;
  const lastSegment = segments.length ? segments[segments.length - 1] : null;
  const lastOut = lastSegment?.clock_out_at || null;
  const isOpen = !!(lastSegment && !lastSegment.clock_out_at);

  const isFuture = day.date > todayYmd;
  const isWeekend = planned === 0 && [5, 6].includes(dayOfWeekIdx(day.date));
  const noPlan = planned === 0 && !isWeekend;

  let status = null;
  if (isOpen) {
    status = "current";
  } else if (planned > 0 && worked === 0 && !isFuture) {
    status = "review";
  } else if (worked > 0) {
    // Tolerancia: incident si difiere más de 15 minutos
    const diff = worked - planned;
    status = Math.abs(diff) > 15 ? "incident" : "ok";
  }

  return {
    ...day,
    weekday: WEEKDAY_SHORT[dayOfWeekIdx(day.date)],
    weekdayLong: WEEKDAY_LONG[dayOfWeekIdx(day.date)],
    dayNumber: ymdToParts(day.date).d,
    inStr: formatTimeHHMM(firstIn, tz),
    outStr: isOpen ? "—" : formatTimeHHMM(lastOut, tz),
    pauseMin: totalBreakMin,
    isFuture,
    isWeekend,
    noPlan,
    isOpen,
    status,
    balance: worked - planned,
  };
}

function HistorialJornadas() {
  document.title = "Historial de jornadas | Kronaly";
  useStartTour("historial");
  const membership = useCurrentMembership();
  const [topTab, setTopTab] = useState("jornadas");
  const [view, setView] = useState("table");
  const [refMonth, setRefMonth] = useState(() => firstOfMonth(`${new Date().getFullYear()}-${pad(new Date().getMonth() + 1)}-01`));
  const [filter, setFilter] = useState("todos");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [correctionDay, setCorrectionDay] = useState(null);
  const [ttSettings, setTtSettings] = useState({ correction_mode: "requires_approval" });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Vista personal: SIEMPRE las jornadas del propio actor.
      // No se pasa employee_profile_id — el backend resuelve el del actor.
      const r = await getJornadaLaboral({ view: "month", date: refMonth });
      setData(unwrap(r));
    } catch (e) {
      setError(typeof e === "string" ? e : e?.message || "No se pudo cargar el historial.");
    } finally {
      setLoading(false);
    }
  }, [refMonth]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    getTimeTrackingSettings()
      .then((r) => {
        if (cancelled) return;
        const data = unwrap(r);
        if (data?.correction_mode) {
          setTtSettings({ correction_mode: data.correction_mode });
        }
      })
      .catch(() => { /* silencioso: usamos default */ });
    return () => { cancelled = true; };
  }, []);

  const tz = data?.timezone || DEFAULT_TZ;
  const todayYmd = useMemo(() => todayYmdInTz(tz), [tz]);

  const daysAll = useMemo(() => {
    if (!data?.days) return [];
    return data.days.map((d) => classifyDay(d, todayYmd, tz));
  }, [data, todayYmd, tz]);

  const incidentsCount = useMemo(
    () => daysAll.filter((d) => d.status === "incident" || d.status === "review").length,
    [daysAll],
  );
  const workedDaysCount = useMemo(
    () => daysAll.filter((d) => (d.worked_minutes || 0) > 0).length,
    [daysAll],
  );

  const filtered = useMemo(() => {
    if (filter === "incidencias") return daysAll.filter((d) => d.status === "incident" || d.status === "review");
    if (filter === "trabajados") return daysAll.filter((d) => (d.worked_minutes || 0) > 0);
    return daysAll;
  }, [daysAll, filter]);

  const totals = data?.totals || { worked_minutes: 0, planned_minutes: 0, balance_minutes: 0 };
  const compliancePct = totals.planned_minutes > 0
    ? Math.round(Math.min(100, (totals.worked_minutes / totals.planned_minutes) * 100))
    : 0;

  const handleOpenDay = (d) => setSelectedDay(d);
  const handleCloseDay = () => setSelectedDay(null);
  const handleOpenCorrection = (d) => setCorrectionDay(d);
  const handleCloseCorrection = () => setCorrectionDay(null);
  const handleAppliedCorrection = useCallback(async () => {
    await load();
    setSelectedDay(null);
  }, [load]);

  const toast = useToast();
  const [pdfState, setPdfState] = useState("idle"); // idle | polling | ready | error
  const pdfJobRef = useRef(null);
  const pdfTimerRef = useRef(null);

  useEffect(() => () => {
    if (pdfTimerRef.current) clearInterval(pdfTimerRef.current);
  }, []);

  const handleGeneratePdf = useCallback(async () => {
    if (!data?.start_date || !data?.end_date) return;
    setPdfState("polling");
    try {
      const r = await enqueueReport({
        type: "employee_history",
        format: "pdf",
        params: { start_date: data.start_date, end_date: data.end_date },
      });
      const job = r?.data ?? r;
      pdfJobRef.current = job.id;

      pdfTimerRef.current = setInterval(async () => {
        try {
          const lr = await listReportJobs({ limit: 5 });
          const jobs = (lr?.data ?? lr) || [];
          const found = jobs.find((j) => j.id === pdfJobRef.current);
          if (!found) return;
          if (found.status === "ready") {
            clearInterval(pdfTimerRef.current);
            pdfTimerRef.current = null;
            setPdfState("idle");
            const range = `${data.start_date}_${data.end_date}`;
            await downloadReportJob(found.id, `historial_${range}.pdf`);
          } else if (found.status === "failed") {
            clearInterval(pdfTimerRef.current);
            pdfTimerRef.current = null;
            setPdfState("idle");
            toast.error("No se pudo generar el PDF", found.failed_reason || "");
          }
        } catch { /* silent poll error */ }
      }, 3000);
    } catch (e) {
      setPdfState("idle");
      toast.error("No se pudo generar el PDF", typeof e === "string" ? e : e?.message || "");
    }
  }, [data, toast]);

  return (
    <div className="kr-page">
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16 }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600, marginBottom: 4 }}>Mi jornada · Histórico</div>
          <h1 style={{ margin: 0, fontFamily: "var(--kr-font-display)", fontSize: 28, fontWeight: 700, letterSpacing: "-0.02em", color: "var(--kr-fg-strong)" }}>Historial de jornadas</h1>
          <p style={{ margin: "6px 0 0", fontSize: 14, color: "var(--kr-fg-muted)" }}>Revisa tus jornadas e identifica incidencias.</p>
        </div>
      </div>

      <Tabs
        tabs={[
          { id: "jornadas", label: "Mis jornadas" },
          { id: "corrections", label: "Mis correcciones" },
        ]}
        value={topTab}
        onChange={setTopTab}
      />

      {topTab === "corrections" ? (
        <BandejaCorrecciones
          isManager={false}
          currentUserId={membership.me?.id || null}
          currentEmployeeProfileId={membership.employeeProfileId}
          tz={tz}
        />
      ) : (
      <>
      <Card padded={false}>
        <div data-tour="historial-filters" style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <MonthPicker value={refMonth} onChange={setRefMonth} />
          <FilterSelect value={filter} onChange={setFilter} />
          <div style={{ flex: 1 }} />
          <Button
            variant="secondary"
            size="sm"
            icon={pdfState === "polling" ? "Loader2" : "FileDown"}
            onClick={handleGeneratePdf}
            disabled={pdfState === "polling" || loading || !data?.start_date}
          >
            {pdfState === "polling" ? "Generando…" : "Generar PDF"}
          </Button>
          <ViewToggle view={view} setView={setView} />
        </div>

        <div data-tour="historial-totals" style={{ borderTop: "1px solid var(--kr-border-subtle)", padding: "10px 16px", display: "flex", gap: 24, alignItems: "center", background: "var(--kr-neutral-50)", flexWrap: "wrap" }}>
          <SummaryStat k="Total trabajado" v={fmtDur(totals.worked_minutes) + " h"} />
          <SummaryStat k="Total esperado"  v={fmtDur(totals.planned_minutes) + " h"} />
          <SummaryStat
            k="Diferencia"
            v={diffStr(totals.balance_minutes)}
            tone={totals.balance_minutes >= 0 ? "success" : "warning"}
          />
          <SummaryStat k="Días trabajados" v={workedDaysCount} mono />
          <SummaryStat k="Incidencias" v={incidentsCount} mono tone={incidentsCount > 0 ? "warning" : "neutral"} />
          <div style={{ flex: 1 }} />
          {totals.planned_minutes > 0 && (
            <Badge tone={compliancePct >= 95 ? "success" : compliancePct >= 80 ? "warning" : "danger"} dot>
              Cumplimiento {compliancePct} %
            </Badge>
          )}
        </div>
      </Card>

      {error && <Alert tone="danger">{error}</Alert>}

      {loading ? (
        <Card>
          <div style={{ padding: 40, textAlign: "center" }}><Spinner size={18} /></div>
        </Card>
      ) : data?.requires_employee_filter ? (
        <Card>
          <EmptyState
            icon="UserCog"
            title="Sin perfil de empleado"
            description="Tu cuenta no tiene perfil de empleado, así que no tiene jornadas propias. Para consultar el historial de otros empleados, ve a RRHH → Jornadas."
          />
        </Card>
      ) : filtered.length === 0 && !error ? (
        <Card>
          <EmptyState
            icon="Calendar"
            title="Sin datos para este periodo"
            description="Cambia de mes o ajusta el filtro para ver registros."
          />
        </Card>
      ) : view === "table" ? (
        <div data-tour="historial-days">
          <TablaJornadas data={filtered} onOpenDay={handleOpenDay} refMonth={refMonth} />
        </div>
      ) : (
        <div data-tour="historial-days">
          <CalendarioMes data={daysAll} onOpenDay={handleOpenDay} refMonth={refMonth} />
        </div>
      )}

      </>
      )}

      <DetalleJornadaDrawer
        day={selectedDay}
        tz={tz}
        onClose={handleCloseDay}
        onCorregir={handleOpenCorrection}
      />
      <CorregirJornadaModal
        open={!!correctionDay}
        day={correctionDay}
        tz={tz}
        correctionMode={ttSettings.correction_mode}
        targetEmployeeProfileId={membership.employeeProfileId}
        isOnBehalf={false}
        onClose={handleCloseCorrection}
        onApplied={handleAppliedCorrection}
      />
    </div>
  );
}


function MonthPicker({ value, onChange }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 10, fontWeight: 600, color: "var(--kr-fg-muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Periodo</span>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <IconBtn icon="ChevronLeft" title="Mes anterior" size={28} onClick={() => onChange(addMonths(value, -1))} />
        <div style={{
          minWidth: 130,
          textAlign: "center",
          fontFamily: "var(--kr-font-sans)",
          fontSize: 13,
          fontWeight: 600,
          color: "var(--kr-fg-strong)",
          padding: "4px 8px",
          border: "1px solid var(--kr-border)",
          borderRadius: 6,
          background: "#fff",
        }}>
          {monthLabel(value)}
        </div>
        <IconBtn icon="ChevronRight" title="Mes siguiente" size={28} onClick={() => onChange(addMonths(value, 1))} />
      </div>
    </div>
  );
}

function FilterSelect({ value, onChange }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 10, fontWeight: 600, color: "var(--kr-fg-muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Filtro</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          height: 32,
          border: "1px solid var(--kr-border)",
          borderRadius: 6,
          padding: "0 10px",
          fontSize: 13,
          color: "var(--kr-fg-strong)",
          background: "#fff",
          outline: "none",
          fontFamily: "var(--kr-font-sans)",
        }}
      >
        <option value="todos">Todos los días</option>
        <option value="incidencias">Solo con incidencia</option>
        <option value="trabajados">Solo trabajados</option>
      </select>
    </label>
  );
}

function SummaryStat({ k, v, tone, mono }) {
  const c = { success: "var(--kr-success-700)", warning: "var(--kr-warning-700)" }[tone] || "var(--kr-fg-strong)";
  return (
    <div>
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--kr-fg-muted)", fontWeight: 600 }}>{k}</div>
      <div style={{ fontFamily: mono ? "var(--kr-font-mono)" : "var(--kr-font-sans)", fontSize: 14, color: c, fontWeight: 700, marginTop: 2 }}>{v}</div>
    </div>
  );
}

function ViewToggle({ view, setView }) {
  return (
    <div style={{ display: "flex", border: "1px solid var(--kr-border)", borderRadius: 6, overflow: "hidden", height: 32 }}>
      {[
        { id: "table", icon: "List", label: "Tabla" },
        { id: "month", icon: "Calendar", label: "Mes" },
      ].map((o) => (
        <button
          key={o.id}
          onClick={() => setView(o.id)}
          style={{
            padding: "0 12px",
            border: "none",
            borderLeft: o.id === "month" ? "1px solid var(--kr-border)" : "none",
            background: view === o.id ? "var(--kr-action-soft-bg)" : "#fff",
            color: view === o.id ? "var(--kr-action-soft-fg)" : "var(--kr-fg-muted)",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Icon name={o.icon} size={12} />
          {o.label}
        </button>
      ))}
    </div>
  );
}

function TablaJornadas({ data, onOpenDay, refMonth }) {
  const { y, m } = ymdToParts(refMonth);
  const monthShort = MONTH_NAMES[m - 1].slice(0, 3);
  return (
    <Card padded={false}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "70px 100px 90px 90px 90px 110px 110px 1fr",
          padding: "10px 20px",
          background: "var(--kr-neutral-50)",
          borderBottom: "1px solid var(--kr-border)",
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          fontWeight: 600,
          color: "var(--kr-fg-muted)",
        }}
      >
        <span>Día</span>
        <span>Fecha</span>
        <span>Entrada</span>
        <span>Salida</span>
        <span>Pausas</span>
        <span>Trabajado</span>
        <span>Diferencia</span>
        <span>Estado</span>
      </div>
      {data.map((d, i) => (
        <FilaJornada
          key={d.date}
          day={d}
          monthShort={monthShort}
          year={y}
          last={i === data.length - 1}
          onOpen={() => onOpenDay && onOpenDay(d)}
        />
      ))}
    </Card>
  );
}

function FilaJornada({ day, monthShort, year, last, onOpen }) {
  const [hover, setHover] = useState(false);
  const isInactive = day.isWeekend || day.noPlan || day.isFuture;
  const meta = day.status ? STATUS_META[day.status] : null;
  const clickable = !isInactive && !!day.record;

  const correctionsCount = day.corrections_count || 0;
  let stateNode;
  if (day.isFuture) stateNode = <span style={{ fontSize: 12, color: "var(--kr-fg-subtle)" }}>—</span>;
  else if (day.isWeekend) stateNode = <span style={{ fontSize: 12, color: "var(--kr-fg-subtle)" }}>Fin de semana</span>;
  else if (day.noPlan) stateNode = <span style={{ fontSize: 12, color: "var(--kr-fg-subtle)" }}>No laborable</span>;
  else if (meta) stateNode = <Badge tone={meta.tone} dot>{meta.label}</Badge>;
  else stateNode = <span style={{ fontSize: 12, color: "var(--kr-fg-subtle)" }}>—</span>;

  const correctedBadge = correctionsCount > 0 ? (
    <Badge
      tone="info"
      dot
      title={correctionsCount === 1
        ? "Esta jornada fue corregida"
        : `Esta jornada fue corregida ${correctionsCount} veces`}
    >
      Corregido{correctionsCount > 1 ? ` ×${correctionsCount}` : ""}
    </Badge>
  ) : null;
  const pendingCount = day.pending_corrections_count || 0;
  const reviewBadge = pendingCount > 0 ? (
    <Badge
      tone="warning"
      dot
      title="Hay una corrección pendiente de aprobación para esta jornada"
    >
      En revisión
    </Badge>
  ) : null;

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={clickable ? onOpen : undefined}
      style={{
        display: "grid",
        gridTemplateColumns: "70px 100px 90px 90px 90px 110px 110px 1fr",
        gap: 0,
        alignItems: "center",
        padding: "11px 20px",
        borderBottom: last ? "none" : "1px solid var(--kr-border-subtle)",
        background: hover && clickable ? "var(--kr-bg-subtle)" : "#fff",
        cursor: clickable ? "pointer" : "default",
        opacity: day.isFuture ? 0.55 : 1,
      }}
    >
      <div>
        <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>{day.weekday}</div>
        <div style={{ fontFamily: "var(--kr-font-mono)", fontSize: 18, fontWeight: 700, color: day.status === "current" ? "var(--kr-action)" : "var(--kr-fg-strong)" }}>{pad(day.dayNumber)}</div>
      </div>
      <div style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>{monthShort} {year}</div>
      <div style={{ fontFamily: "var(--kr-font-mono)", fontSize: 13, color: "var(--kr-fg-strong)" }}>{day.inStr}</div>
      <div style={{ fontFamily: "var(--kr-font-mono)", fontSize: 13, color: "var(--kr-fg-strong)" }}>{day.outStr}</div>
      <div style={{ fontFamily: "var(--kr-font-mono)", fontSize: 13, color: "var(--kr-fg-muted)" }}>{day.pauseMin > 0 ? `${day.pauseMin} min` : "—"}</div>
      <div style={{ fontFamily: "var(--kr-font-mono)", fontSize: 13, color: "var(--kr-fg-strong)", fontWeight: 600 }}>{day.worked_minutes > 0 ? fmtDur(day.worked_minutes) : "—"}</div>
      <div
        style={{
          fontFamily: "var(--kr-font-mono)",
          fontSize: 13,
          fontWeight: 600,
          color:
            day.planned_minutes === 0 || day.isFuture
              ? "var(--kr-fg-subtle)"
              : day.balance > 0
              ? "var(--kr-success-700)"
              : day.balance < 0
              ? "var(--kr-warning-700)"
              : "var(--kr-fg-strong)",
        }}
      >
        {day.planned_minutes === 0 || day.isFuture ? "—" : diffStr(day.balance)}
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
        {stateNode}
        {reviewBadge}
        {correctedBadge}
      </div>
    </div>
  );
}


function DetalleJornadaDrawer({ day, tz, onClose, onCorregir }) {
  const isOpen = !!day;

  const titleNode = day ? (
    <div>
      <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600 }}>Detalle de jornada</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: "var(--kr-fg-strong)", marginTop: 2 }}>
        {day.weekdayLong} · {day.dayNumber} {MONTH_NAMES[ymdToParts(day.date).m - 1].toLowerCase()} {ymdToParts(day.date).y}
      </div>
    </div>
  ) : null;

  return (
    <Drawer open={isOpen} onClose={onClose} title={titleNode} size="lg">
      {day && <DetalleJornadaBody day={day} tz={tz} onCorregir={onCorregir} />}
    </Drawer>
  );
}

function DetalleJornadaBody({ day, tz, onCorregir }) {
  const meta = day.status ? STATUS_META[day.status] : null;
  const segments = day.record?.segments || [];
  const breaks = day.record?.breaks || [];
  const events = [];
  segments.forEach((s, idx) => {
    events.push({ time: formatTimeHHMM(s.clock_in_at, tz), label: `Marcaje de entrada${segments.length > 1 ? ` #${idx + 1}` : ""}`, icon: "Play", tone: "success" });
    if (s.clock_out_at) {
      events.push({ time: formatTimeHHMM(s.clock_out_at, tz), label: `Marcaje de salida${segments.length > 1 ? ` #${idx + 1}` : ""}`, icon: "Square", tone: "info" });
    } else {
      events.push({ time: "en curso", label: "Jornada abierta", icon: "Clock", tone: "info", live: true });
    }
  });
  breaks.forEach((b, idx) => {
    events.push({ time: formatTimeHHMM(b.start_at, tz), label: `Inicio de pausa${breaks.length > 1 ? ` #${idx + 1}` : ""}`, icon: "Coffee", tone: "warning" });
    events.push({ time: formatTimeHHMM(b.end_at, tz), label: "Fin de pausa", icon: "Play", tone: "neutral" });
  });
  events.sort((a, b) => (a.time > b.time ? 1 : -1));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {meta && (
        <div
          style={{
            padding: 14,
            borderRadius: 8,
            background:
              meta.tone === "success" ? "var(--kr-status-success-bg)"
                : meta.tone === "warning" ? "var(--kr-status-warning-bg)"
                : meta.tone === "danger" ? "var(--kr-status-danger-bg)"
                : "var(--kr-status-info-bg)",
            display: "flex",
            gap: 12,
            alignItems: "flex-start",
          }}
        >
          <Icon
            name={meta.icon}
            size={18}
            style={{
              flexShrink: 0,
              marginTop: 2,
              color:
                meta.tone === "success" ? "var(--kr-success-700)"
                  : meta.tone === "warning" ? "var(--kr-warning-700)"
                  : meta.tone === "danger" ? "var(--kr-danger-700)"
                  : "var(--kr-info-700)",
            }}
          />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--kr-fg-strong)" }}>{meta.label}</div>
            {day.status === "incident" && (
              <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", marginTop: 2 }}>
                Diferencia respecto al horario esperado: {diffStr(day.balance)}.
              </div>
            )}
            {day.status === "review" && (
              <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", marginTop: 2 }}>
                No se ha registrado marcaje para esta jornada.
              </div>
            )}
          </div>
        </div>
      )}

      {(day.pending_corrections_count || 0) > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Badge tone="warning" dot>En revisión</Badge>
          <span style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>
            Hay una corrección pendiente de aprobación para esta jornada.
          </span>
        </div>
      )}

      {(day.corrections_count || 0) > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Badge
            tone="info"
            dot
            title={day.corrections_count === 1
              ? "Esta jornada fue corregida"
              : `Esta jornada fue corregida ${day.corrections_count} veces`}
          >
            Corregido{day.corrections_count > 1 ? ` ×${day.corrections_count}` : ""}
          </Badge>
          <span style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>
            Los marcajes mostrados reflejan la última corrección aplicada.
          </span>
        </div>
      )}

      {onCorregir && !day.isFuture && (
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          padding: "10px 14px",
          border: "1px dashed var(--kr-border)",
          borderRadius: 6,
          background: "var(--kr-bg-surface)",
        }}>
          <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", lineHeight: 1.4 }}>
            {day.is_closed ? (
              <>
                <strong>Mes cerrado.</strong> Esta jornada pertenece a un mes cerrado y no admite
                correcciones. Pide al owner que reabra el mes desde Configuración si necesitas modificarla.
              </>
            ) : (
              <>
                ¿Olvidaste marcar a tiempo? Puedes corregir los marcajes de esta jornada.
                La corrección queda registrada en el histórico de auditoría.
              </>
            )}
          </div>
          <Button
            variant="secondary"
            size="sm"
            icon={day.is_closed ? "Lock" : "Pencil"}
            onClick={() => !day.is_closed && onCorregir(day)}
            disabled={day.is_closed}
          >
            Corregir jornada
          </Button>
        </div>
      )}

      <Card>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }}>
          <BigStat k="Entrada" v={day.inStr} mono />
          <BigStat k="Salida" v={day.outStr} mono />
          <BigStat k="Pausas" v={day.pauseMin > 0 ? `${day.pauseMin} min` : "—"} mono />
          <BigStat
            k="Trabajado neto"
            v={day.worked_minutes > 0 ? fmtDur(day.worked_minutes) + " h" : "—"}
            mono
            tone={day.balance < 0 ? "warning" : "success"}
          />
        </div>
        <div style={{ marginTop: 14, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", background: "var(--kr-neutral-50)", borderRadius: 6 }}>
          <span style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>Horario esperado</span>
          <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 12, color: "var(--kr-fg-strong)", fontWeight: 600 }}>
            {day.planned_minutes > 0 ? `${fmtDur(day.planned_minutes)} h` : "—"}
          </span>
          <span style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>·</span>
          <span style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>Diferencia</span>
          <span
            style={{
              fontFamily: "var(--kr-font-mono)",
              fontSize: 12,
              fontWeight: 700,
              color: day.planned_minutes === 0
                ? "var(--kr-fg-subtle)"
                : day.balance >= 0
                  ? "var(--kr-success-700)"
                  : "var(--kr-warning-700)",
            }}
          >
            {day.planned_minutes > 0 ? diffStr(day.balance) : "—"}
          </span>
        </div>
      </Card>

      <Card>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--kr-fg-strong)", marginBottom: 12 }}>Cronología de eventos</div>
        {events.length === 0 ? (
          <EmptyState icon="Clock" title="Sin eventos" description="Este día no tiene marcajes registrados." />
        ) : (
          <DayTimeline events={events} />
        )}
      </Card>

      {segments.length > 0 && (
        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--kr-fg-strong)", marginBottom: 10 }}>Segmentos ({segments.length})</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {segments.map((s, i) => {
              let netMin = s.duration_minutes;
              if (netMin != null && breaks.length > 0) {
                const segStart = new Date(s.clock_in_at).getTime();
                const segEnd = new Date(s.clock_out_at).getTime();
                for (const b of breaks) {
                  const bStart = Math.max(segStart, new Date(b.start_at).getTime());
                  const bEnd = Math.min(segEnd, new Date(b.end_at).getTime());
                  if (bEnd > bStart) netMin -= Math.round((bEnd - bStart) / 60000);
                }
                netMin = Math.max(0, netMin);
              }
              return (
                <div key={s.id || i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: 10, border: "1px solid var(--kr-border-subtle)", borderRadius: 6 }}>
                  <div style={{ fontFamily: "var(--kr-font-mono)", fontSize: 13, color: "var(--kr-fg-strong)" }}>
                    {formatTimeHHMM(s.clock_in_at, tz)} – {s.clock_out_at ? formatTimeHHMM(s.clock_out_at, tz) : "en curso"}
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {s.overtime_minutes > 0 && (
                      <Badge tone="warning" dot>Overtime {s.overtime_minutes} min</Badge>
                    )}
                    {netMin != null && (
                      <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 12, color: "var(--kr-fg-muted)" }}>
                        {fmtDur(netMin)} h
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}

function BigStat({ k, v, mono, tone }) {
  const c = { success: "var(--kr-success-700)", warning: "var(--kr-warning-700)" }[tone] || "var(--kr-fg-strong)";
  return (
    <div>
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--kr-fg-muted)", fontWeight: 600 }}>{k}</div>
      <div style={{ fontFamily: mono ? "var(--kr-font-mono)" : "var(--kr-font-sans)", fontSize: 20, fontWeight: 700, color: c, marginTop: 4 }}>{v}</div>
    </div>
  );
}

function DayTimeline({ events }) {
  const tones = {
    success: "var(--kr-success-500)",
    warning: "var(--kr-warning-500)",
    info: "var(--kr-info-500)",
    danger: "var(--kr-danger-500)",
    neutral: "var(--kr-neutral-400)",
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", position: "relative" }}>
      <span style={{ position: "absolute", left: 13, top: 12, bottom: 12, width: 1, background: "var(--kr-border-subtle)" }} />
      {events.map((e, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", position: "relative" }}>
          <span
            style={{
              width: 26,
              height: 26,
              borderRadius: 999,
              background: "#fff",
              border: `2px solid ${tones[e.tone]}`,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              color: tones[e.tone],
              zIndex: 1,
              boxShadow: e.live ? `0 0 0 3px ${tones[e.tone]}33` : "none",
            }}
          >
            <Icon name={e.icon} size={12} />
          </span>
          <div style={{ flex: 1, fontSize: 13, fontWeight: 500, color: "var(--kr-fg-strong)" }}>{e.label}</div>
          <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 12, color: "var(--kr-fg-muted)", fontVariantNumeric: "tabular-nums" }}>{e.time}</span>
        </div>
      ))}
    </div>
  );
}

export default HistorialJornadas;
